import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import AppLoader from '../../../components/AppLoader';
import YearSelector from '../../../components/Common/YearSelector';

const toDateOnlyString = (d) => {
  try {
    if (!d) return null;
    const date = (d instanceof Date) ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  } catch (_) {
    return null;
  }
};

const buildQueryString = (params = {}) => {
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qp.set(k, String(v));
  });
  const s = qp.toString();
  return s ? `?${s}` : '';
};

export default function AttendanceSummaryTabsPage() {
  const { t } = useTranslation();

  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('students');

  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentsList, setStudentsList] = useState([]);
  const [studentsListLoading, setStudentsListLoading] = useState(false);

  const [subjectsCatalog, setSubjectsCatalog] = useState([]);
  const [classSubjectsLite, setClassSubjectsLite] = useState([]);
  const [subjectsOptionsLoading, setSubjectsOptionsLoading] = useState(false);

  const levels = useMemo(() => {
    const set = new Set();
    (classes || []).forEach((c) => {
      if (c?.level) set.add(String(c.level));
    });
    return Array.from(set);
  }, [classes]);

  const defaultMonthRange = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: toDateOnlyString(from), to: toDateOnlyString(to) };
  }, []);

  const [studentsLevel, setStudentsLevel] = useState('');
  const [studentsClassId, setStudentsClassId] = useState('');
  const [studentsStudentId, setStudentsStudentId] = useState('');
  const [studentsStartDate, setStudentsStartDate] = useState(defaultMonthRange.from || '');
  const [studentsEndDate, setStudentsEndDate] = useState(defaultMonthRange.to || '');
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsSummary, setStudentsSummary] = useState(null);
  const [studentsDetail, setStudentsDetail] = useState(null);

  const [teachersMonth, setTeachersMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [teachersTeacherId, setTeachersTeacherId] = useState('');
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersSummary, setTeachersSummary] = useState(null);
  const [teachersDetail, setTeachersDetail] = useState(null);

  const [subjectsLevel, setSubjectsLevel] = useState('');
  const [subjectsClassId, setSubjectsClassId] = useState('');
  const [subjectsSubjectId, setSubjectsSubjectId] = useState('');
  const [subjectsStartDate, setSubjectsStartDate] = useState(defaultMonthRange.from || '');
  const [subjectsEndDate, setSubjectsEndDate] = useState(defaultMonthRange.to || '');
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsSummary, setSubjectsSummary] = useState(null);
  const [subjectsDetail, setSubjectsDetail] = useState(null);

  const loadClasses = useCallback(async () => {
    const res = await fetchJson('/api/classes');
    const list = res?.classes || res?.data || res || [];
    setClasses(Array.isArray(list) ? list : []);
  }, []);

  const loadTeachers = useCallback(async () => {
    const res = await fetchJson('/api/attendance/teachers');
    const list = res?.data || res?.teachers || res || [];
    setTeachers(Array.isArray(list) ? list : []);
  }, []);

  const loadStudentsForClass = useCallback(async (classId) => {
    try {
      if (!classId) {
        setStudentsList([]);
        return;
      }
      setStudentsListLoading(true);
      const qs = buildQueryString({ classId: String(classId) });
      const res = await fetchJson(`/api/students/for-allocations${qs}`);
      const list = res?.students || res?.data || res || [];
      setStudentsList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setStudentsList([]);
    } finally {
      setStudentsListLoading(false);
    }
  }, []);

  const loadSubjectsCatalogAndAssignments = useCallback(async () => {
    try {
      setSubjectsOptionsLoading(true);
      const [subjectsRes, classSubjectsRes] = await Promise.all([
        fetchJson('/api/subjects/for-allocations'),
        fetchJson('/api/class-subjects/lite')
      ]);
      const subjectsList = subjectsRes?.subjects || subjectsRes?.data || subjectsRes || [];
      const classSubjectsList = classSubjectsRes?.rows || classSubjectsRes?.data || classSubjectsRes || [];
      setSubjectsCatalog(Array.isArray(subjectsList) ? subjectsList : []);
      setClassSubjectsLite(Array.isArray(classSubjectsList) ? classSubjectsList : []);
    } catch (e) {
      console.error(e);
      setSubjectsCatalog([]);
      setClassSubjectsLite([]);
    } finally {
      setSubjectsOptionsLoading(false);
    }
  }, []);

  const loadStudentAbsences = useCallback(async (opts = {}) => {
    try {
      setStudentsLoading(true);
      setError(null);

      const qs = buildQueryString({
        level: studentsLevel || null,
        classId: studentsClassId || null,
        studentId: (opts.studentId ?? studentsStudentId) || null,
        startDate: studentsStartDate || null,
        endDate: studentsEndDate || null
      });

      const data = await fetchJson(`/api/attendance/summary/students${qs}`);
      if (opts.studentId) setStudentsDetail(data);
      else setStudentsSummary(data);
    } catch (e) {
      console.error(e);
      setError(t('attendance.failedLoadSummary') || 'Failed to load attendance summary');
      if (opts.studentId) setStudentsDetail(null);
      else setStudentsSummary(null);
    } finally {
      setStudentsLoading(false);
    }
  }, [studentsLevel, studentsClassId, studentsStudentId, studentsStartDate, studentsEndDate, t]);

  const loadTeacherMonthly = useCallback(async (opts = {}) => {
    try {
      setTeachersLoading(true);
      setError(null);

      const [yRaw, mRaw] = String(opts.month || teachersMonth || '').split('-');
      const y = Number(yRaw);
      const m = Number(mRaw);
      const start = (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12)
        ? `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`
        : null;
      const end = (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12)
        ? `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
        : null;

      if (!start || !end) {
        setTeachersSummary(null);
        setTeachersDetail(null);
        setError(`${t('attendance.month') || 'Month'}: ${t('attendance.required') || 'required'}`);
        return;
      }

      const qs = buildQueryString({
        startDate: start,
        endDate: end,
        teacherId: (opts.teacherId ?? teachersTeacherId) || null
      });

      const data = await fetchJson(`/api/attendance/summary/teachers-monthly${qs}`);
      if (opts.teacherId) setTeachersDetail(data);
      else setTeachersSummary(data);
    } catch (e) {
      console.error(e);
      const msg = e?.data?.error || e?.message || (t('attendance.failedLoadSummary') || 'Failed to load attendance summary');
      setError(msg);
      if (opts.teacherId) setTeachersDetail(null);
      else setTeachersSummary(null);
    } finally {
      setTeachersLoading(false);
    }
  }, [teachersMonth, teachersTeacherId, t]);

  const loadSubjectsSummary = useCallback(async (opts = {}) => {
    try {
      if (!subjectsClassId) {
        setSubjectsSummary(null);
        setSubjectsDetail(null);
        return;
      }
      setSubjectsLoading(true);
      setError(null);

      const qs = buildQueryString({
        classId: subjectsClassId,
        subjectId: opts.subjectId || null,
        startDate: subjectsStartDate || null,
        endDate: subjectsEndDate || null
      });

      const data = await fetchJson(`/api/attendance/summary/subjects${qs}`);
      if (opts.subjectId) setSubjectsDetail(data);
      else setSubjectsSummary(data);
    } catch (e) {
      console.error(e);
      setError(t('attendance.failedLoadSummary') || 'Failed to load attendance summary');
      if (opts.subjectId) setSubjectsDetail(null);
      else setSubjectsSummary(null);
    } finally {
      setSubjectsLoading(false);
    }
  }, [subjectsClassId, subjectsStartDate, subjectsEndDate, t]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setBootLoading(true);
        await Promise.all([loadClasses(), loadTeachers(), loadSubjectsCatalogAndAssignments()]);
      } finally {
        if (mounted) setBootLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [loadClasses, loadTeachers, loadSubjectsCatalogAndAssignments]);

  useEffect(() => {
    loadStudentsForClass(studentsClassId);
  }, [studentsClassId, loadStudentsForClass]);

  useScopeAutoRefresh(() => {
    return (async () => {
      await Promise.all([
        loadClasses(),
        loadTeachers(),
        loadSubjectsCatalogAndAssignments(),
        loadStudentsForClass(studentsClassId)
      ]);

      if (tab === 'students') {
        if (studentsStudentId) return loadStudentAbsences({ studentId: studentsStudentId });
        return loadStudentAbsences();
      }
      if (tab === 'teachers') return loadTeacherMonthly();
      if (tab === 'subjects') {
        if (subjectsSubjectId) {
          await loadSubjectsSummary();
          return loadSubjectsSummary({ subjectId: subjectsSubjectId });
        }
        return loadSubjectsSummary();
      }
      return null;
    })();
  }, [
    tab,
    loadClasses,
    loadTeachers,
    loadSubjectsCatalogAndAssignments,
    loadStudentsForClass,
    studentsClassId,
    studentsStudentId,
    subjectsSubjectId,
    loadStudentAbsences,
    loadTeacherMonthly,
    loadSubjectsSummary
  ]);

  const header = (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <Typography variant="h5">{t('attendance.summaryTitle') || 'Attendance Summary'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <YearSelector label={t('attendance.academicYear') || 'Academic Year'} fullWidth />
        </Grid>
      </Grid>
    </Paper>
  );

  const studentsRows = Array.isArray(studentsSummary?.students) ? studentsSummary.students : [];
  const teachersRows = Array.isArray(teachersSummary?.rows) ? teachersSummary.rows : [];
  const subjectsRows = Array.isArray(subjectsSummary?.subjects) ? subjectsSummary.subjects : [];

  const studentsOptions = useMemo(() => {
    const list = Array.isArray(studentsList) ? studentsList : [];
    const mapped = list.map((s) => {
      const name = `${s?.firstName || ''} ${s?.lastName || ''}`.trim() || s?.name || (s?.id ? `Student ${s.id}` : '');
      return {
        id: s?.id ? String(s.id) : '',
        name,
        studentNumber: s?.studentNumber || ''
      };
    }).filter((s) => s.id);
    mapped.sort((a, b) => a.name.localeCompare(b.name));
    return mapped;
  }, [studentsList]);

  const subjectsOptions = useMemo(() => {
    if (!subjectsClassId) return [];
    const classIdStr = String(subjectsClassId);
    const assignments = Array.isArray(classSubjectsLite) ? classSubjectsLite : [];
    const ids = new Set(
      assignments
        .filter((a) => String(a?.classId || '') === classIdStr)
        .map((a) => String(a?.subjectId || ''))
        .filter(Boolean)
    );

    const subs = Array.isArray(subjectsCatalog) ? subjectsCatalog : [];
    const map = new Map(subs.map((s) => [String(s?.id || ''), s]));

    const out = Array.from(ids).map((id) => {
      const s = map.get(id);
      return {
        id,
        name: s?.name || s?.label || s?.code || `Subject ${id}`
      };
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [subjectsClassId, classSubjectsLite, subjectsCatalog]);

  if (bootLoading) return <AppLoader />;

  return (
    <Box sx={{ p: 2 }}>
      {header}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab value="students" label={t('attendance.tabsStudents') || 'Student Absences'} />
          <Tab value="teachers" label={t('attendance.tabsTeachers') || 'Teachers Attendance'} />
          <Tab value="subjects" label={t('attendance.tabsSubjects') || 'Attendance by Subjects'} />
        </Tabs>
      </Paper>

      {tab === 'students' ? (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.level') || 'Level'}</InputLabel>
                  <Select
                    value={studentsLevel}
                    label={t('attendance.level') || 'Level'}
                    onChange={(e) => {
                      setStudentsLevel(e.target.value);
                      setStudentsClassId('');
                      setStudentsStudentId('');
                      setStudentsSummary(null);
                      setStudentsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allLevels') || 'All levels'}</MenuItem>
                    {levels.map((lv) => <MenuItem key={lv} value={lv}>{lv}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.selectClass') || 'Select Class'}</InputLabel>
                  <Select
                    value={studentsClassId}
                    label={t('attendance.selectClass') || 'Select Class'}
                    onChange={(e) => {
                      setStudentsClassId(e.target.value);
                      setStudentsStudentId('');
                      setStudentsSummary(null);
                      setStudentsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allClasses') || 'All classes'}</MenuItem>
                    {(classes || [])
                      .filter((c) => !studentsLevel || String(c?.level || '') === String(studentsLevel))
                      .map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {`${c.name || ''}${c.section ? ` ${c.section}` : ''}`.trim()}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small" disabled={!studentsClassId || studentsListLoading}>
                  <InputLabel>{t('attendance.selectStudent') || 'Select Student'}</InputLabel>
                  <Select
                    value={studentsStudentId}
                    label={t('attendance.selectStudent') || 'Select Student'}
                    onChange={(e) => {
                      setStudentsStudentId(e.target.value);
                      setStudentsSummary(null);
                      setStudentsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allStudents') || 'All students'}</MenuItem>
                    {studentsOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {`${s.name}${s.studentNumber ? ` (${s.studentNumber})` : ''}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  type="date"
                  label={t('attendance.from') || 'From'}
                  value={studentsStartDate}
                  onChange={(e) => setStudentsStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  type="date"
                  label={t('attendance.to') || 'To'}
                  value={studentsEndDate}
                  onChange={(e) => setStudentsEndDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => (studentsStudentId ? loadStudentAbsences({ studentId: studentsStudentId }) : loadStudentAbsences())}
                  fullWidth
                >
                  {t('attendance.refresh') || 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {studentsLoading ? <AppLoader /> : null}

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attendance.student') || 'Student'}</TableCell>
                  <TableCell>{t('attendance.studentNumber') || 'Number'}</TableCell>
                  <TableCell>{t('attendance.selectClass') || 'Class'}</TableCell>
                  <TableCell>{t('attendance.absentTime') || 'Absent (h:min)'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentsRows.map((r) => (
                  <TableRow
                    key={r.studentId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => loadStudentAbsences({ studentId: r.studentId })}
                  >
                    <TableCell>{r.studentName}</TableCell>
                    <TableCell>{r.studentNumber}</TableCell>
                    <TableCell>{r.className}</TableCell>
                    <TableCell>{r.absentTimeLabel}</TableCell>
                  </TableRow>
                ))}
                {!studentsRows.length && studentsSummary && !studentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('attendance.noData') || 'No attendance data available for the selected period and class.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {studentsDetail ? (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('attendance.studentBreakdown') || 'Student Breakdown'}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('attendance.studentAbsenceBySubject') || 'Student Absence (per subject)'}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('attendance.subject') || 'Subject'}</TableCell>
                    <TableCell>{t('attendance.absentTime') || 'Absent (h:min)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(studentsDetail.subjectBreakdown || []).map((s) => (
                    <TableRow key={String(s.subjectId || 'none')}>
                      <TableCell>{s.subjectName}</TableCell>
                      <TableCell>{s.absentTimeLabel}</TableCell>
                    </TableRow>
                  ))}
                  {!studentsDetail.subjectBreakdown?.length ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" color="text.secondary">
                          {t('attendance.noSubjectAbsence') || 'No subject absence data available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          ) : null}
        </Box>
      ) : null}

      {tab === 'teachers' ? (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('attendance.month') || 'Month'}
                  type="month"
                  value={teachersMonth}
                  onChange={(e) => {
                    setTeachersMonth(e.target.value);
                    setTeachersSummary(null);
                    setTeachersDetail(null);
                  }}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.selectTeacher') || 'Select Teacher'}</InputLabel>
                  <Select
                    value={teachersTeacherId}
                    label={t('attendance.selectTeacher') || 'Select Teacher'}
                    onChange={(e) => {
                      setTeachersTeacherId(e.target.value);
                      setTeachersSummary(null);
                      setTeachersDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allTeachers') || 'All teachers'}</MenuItem>
                    {(teachers || []).map((tr) => (
                      <MenuItem key={tr.id} value={String(tr.id)}>
                        {tr.name || `${tr.firstName || ''} ${tr.lastName || ''}`.trim() || `Teacher ${tr.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => loadTeacherMonthly()} fullWidth>
                  {t('attendance.refresh') || 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {teachersLoading ? <AppLoader /> : null}

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attendance.teacher') || 'Teacher'}</TableCell>
                  <TableCell>{t('attendance.month') || 'Month'}</TableCell>
                  <TableCell>{t('attendance.taughtTime') || 'Taught time'}</TableCell>
                  <TableCell>{t('attendance.submissionsCount') || 'Submissions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teachersRows.map((r) => (
                  <TableRow
                    key={`${r.teacherId}-${r.month}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => loadTeacherMonthly({ teacherId: r.teacherId, month: r.month })}
                  >
                    <TableCell>{r.teacherName}</TableCell>
                    <TableCell>{r.month}</TableCell>
                    <TableCell>{r.taughtTimeLabel}</TableCell>
                    <TableCell>{r.submissionsCount}</TableCell>
                  </TableRow>
                ))}
                {!teachersRows.length && teachersSummary && !teachersLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('attendance.noTeacherSessions') || 'No teacher sessions found for this selection.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {teachersDetail ? (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('attendance.teacherSummary') || 'Teacher Summary'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('attendance.date') || 'Date'}</TableCell>
                      <TableCell>{t('attendance.selectClass') || 'Class'}</TableCell>
                      <TableCell>{t('attendance.subject') || 'Subject'}</TableCell>
                      <TableCell>{t('attendance.period') || 'Period'}</TableCell>
                      <TableCell>{t('attendance.duration') || 'Duration'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(teachersDetail.teacherSessions || []).map((s, idx) => (
                      <TableRow key={`${s.teacherId}-${s.date}-${idx}`}>
                        <TableCell>{s.date}</TableCell>
                        <TableCell>{s.className}</TableCell>
                        <TableCell>{s.subjectName || ''}</TableCell>
                        <TableCell>{s.period || ''}</TableCell>
                        <TableCell>{s.durationMinutes ? `${Math.floor(s.durationMinutes / 60)}h ${String(s.durationMinutes % 60).padStart(2, '0')}m` : ''}</TableCell>
                      </TableRow>
                    ))}
                    {!teachersDetail.teacherSessions?.length ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            {t('attendance.noTeacherSessions') || 'No teacher sessions found for this selection.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : null}
        </Box>
      ) : null}

      {tab === 'subjects' ? (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.level') || 'Level'}</InputLabel>
                  <Select
                    value={subjectsLevel}
                    label={t('attendance.level') || 'Level'}
                    onChange={(e) => {
                      setSubjectsLevel(e.target.value);
                      setSubjectsClassId('');
                      setSubjectsSubjectId('');
                      setSubjectsSummary(null);
                      setSubjectsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allLevels') || 'All levels'}</MenuItem>
                    {levels.map((lv) => <MenuItem key={lv} value={lv}>{lv}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.selectClass') || 'Select Class'}</InputLabel>
                  <Select
                    value={subjectsClassId}
                    label={t('attendance.selectClass') || 'Select Class'}
                    onChange={(e) => {
                      setSubjectsClassId(e.target.value);
                      setSubjectsSubjectId('');
                      setSubjectsSummary(null);
                      setSubjectsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.selectClass') || 'Select Class'}</MenuItem>
                    {(classes || [])
                      .filter((c) => !subjectsLevel || String(c?.level || '') === String(subjectsLevel))
                      .map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {`${c.name || ''}${c.section ? ` ${c.section}` : ''}`.trim()}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={!subjectsClassId || subjectsOptionsLoading}>
                  <InputLabel>{t('attendance.selectSubject') || 'Select Subject'}</InputLabel>
                  <Select
                    value={subjectsSubjectId}
                    label={t('attendance.selectSubject') || 'Select Subject'}
                    onChange={(e) => {
                      setSubjectsSubjectId(e.target.value);
                      setSubjectsSummary(null);
                      setSubjectsDetail(null);
                    }}
                  >
                    <MenuItem value="">{t('attendance.allSubjects') || 'All subjects'}</MenuItem>
                    {subjectsOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  type="date"
                  label={t('attendance.from') || 'From'}
                  value={subjectsStartDate}
                  onChange={(e) => setSubjectsStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  type="date"
                  label={t('attendance.to') || 'To'}
                  value={subjectsEndDate}
                  onChange={(e) => setSubjectsEndDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    if (!subjectsSubjectId) return loadSubjectsSummary();
                    return (async () => {
                      await loadSubjectsSummary();
                      await loadSubjectsSummary({ subjectId: subjectsSubjectId });
                    })();
                  }}
                  disabled={!subjectsClassId}
                  fullWidth
                >
                  {t('attendance.refresh') || 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {!subjectsClassId ? <Alert severity="info">{t('attendance.selectClass') || 'Select Class'}</Alert> : null}

          {subjectsLoading ? <AppLoader /> : null}

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attendance.subject') || 'Subject'}</TableCell>
                  <TableCell>{t('attendance.absentTime') || 'Absent (h:min)'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectsRows.map((r) => (
                  <TableRow
                    key={String(r.subjectId || 'none')}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSubjectsSubjectId(String(r.subjectId || ''));
                      loadSubjectsSummary({ subjectId: r.subjectId });
                    }}
                  >
                    <TableCell>{r.subjectName}</TableCell>
                    <TableCell>{r.absentTimeLabel}</TableCell>
                  </TableRow>
                ))}
                {!subjectsRows.length && subjectsSummary && !subjectsLoading ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        {t('attendance.noSubjectAbsence') || 'No subject absence data available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {subjectsDetail ? (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('attendance.studentBreakdown') || 'Student Breakdown'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('attendance.student') || 'Student'}</TableCell>
                    <TableCell>{t('attendance.studentNumber') || 'Number'}</TableCell>
                    <TableCell>{t('attendance.absentTime') || 'Absent (h:min)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(subjectsDetail.students || []).map((s) => (
                    <TableRow key={String(s.studentId || 'none')}>
                      <TableCell>{s.studentName}</TableCell>
                      <TableCell>{s.studentNumber}</TableCell>
                      <TableCell>{s.absentTimeLabel}</TableCell>
                    </TableRow>
                  ))}
                  {!subjectsDetail.students?.length ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          {t('attendance.noStudentStats') || 'No student statistics available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
